import { ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { UsersService } from '../../../shared/services/users/users.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { QeqService } from '../../../shared/services/qeq/qeq.service';
import { QeqUser } from '../../../shared/interfaces/qeq.interface';

@Component({
  selector: 'app-list-users',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    TitleComponent,
    RouterOutlet,
    LoaderComponent,
    QeqPipe,
  ],
  templateUrl: './list-users.component.html',
  styleUrls: [
    './list-users.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class ListUsersComponent {
  title: string = 'Usuarios';
  subtitle: string = 'Gestiona los usuarios del sistema.';
  isUuid: boolean = false;
  filterControl = new FormControl('');

  // For managing subscriptions
  private destroy$ = new Subject<void>();
  private routerSubscription: Subscription | null = null;
  private dataLoaded = false;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private usersService: UsersService,
    private storageService: StorageService,
    private qeqService: QeqService
  ) {
    // Setup router event listener once in the constructor
    this.setupRouterEvents();
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  readonly dialog = inject(MatDialog);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = [
    'username',
    // 'email',
    'external_id',
    'acciones',
  ];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    // Set up custom sort accessor for username column
    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'username':
          // Get the display name (same logic as in sorting)
          const qeqData = this.qeqService['getQeqFromStorage']?.();
          if (qeqData && qeqData.data && data.user?.username) {
            const qeqUser = qeqData.data.find(
              (u: QeqUser) => u.personnelnumber === data.user.username
            );
            return qeqUser
              ? qeqUser.name.toLowerCase()
              : data.user?.username?.toLowerCase() || '';
          }
          return data.user?.username?.toLowerCase() || '';
        case 'external_id':
          return data.external_id;
        default:
          return data[sortHeaderId];
      }
    };
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, username: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar este usuario?',
        message: username,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          // Replace with appropriate delete method when available
          // return this.rolesService.deleteRole(id);
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar la tabla
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dataSource.data = this.dataSource.data.filter(
            (user) => user.id !== id
          );
        }
      });
  }

  getEmployees() {
    return this.usersService.getEmployees();
  }
  myroles: any = {}; // Change from array to object

  setupRouterEvents() {
    // Setup router subscription only once
    this.routerSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        // Update the isUuid flag based on URL
        this.updateViewState();

        // Only load data when we're on the main policies page AND data isn't already loaded
        if (!this.isUuid && !this.dataLoaded) {
          this.loadData();
        }
      });
  }

  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'users_list',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      //  console.log(
      //    'No user roles found in storage or no permissions for Usuarios'
      //  );
    }
  }

  updateViewState() {
    const currentUrl = this.router.url;
    if (
      currentUrl.includes('/users/list/edit') ||
      currentUrl.includes('/users/list/add')
    ) {
      this.isUuid = true;
    } else if (currentUrl.includes('/users/list')) {
      this.isUuid = false;
      // Set dataLoaded to false when returning to main view to force reload
      if (this.dataLoaded) {
        this.dataLoaded = false;
      }
    }
    this.cdr.detectChanges();
  }

  ngOnInit() {
    // Check if we're on the main policies page
    if (!this.isUuid && !this.dataLoaded) {
      this.loadData();
    }

    // Set up filter
    this.setupFilter();

    this.loadRoles();
  }

  setupFilter() {
    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.applyFilter(value || '');
      });
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim().toLowerCase();

    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const username = data.user?.username?.toLowerCase() || '';
      const email = data.user?.email?.toLowerCase() || '';
      const externalId = data.external_id?.toString().toLowerCase() || '';

      // Get the QeQ name for this user
      const qeqData = this.qeqService['getQeqFromStorage']?.();
      let qeqName = '';
      if (qeqData && qeqData.data && data.user?.username) {
        const qeqUser = qeqData.data.find(
          (u: QeqUser) => u.personnelnumber === data.user.username
        );
        qeqName = qeqUser ? qeqUser.name.toLowerCase() : '';
      }

      return (
        username.includes(filter) ||
        email.includes(filter) ||
        externalId.includes(filter) ||
        qeqName.includes(filter)
      );
    };

    this.dataSource.filter = filterValue;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  loadData() {
    if (this.dataLoaded) return; // Prevent duplicate loading

    this.loaderService.showLoader();

    // Load employees data
    this.getEmployees()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (employees) => {
          // Sort employees by name alphabetically (A-Z)
          const sortedEmployees = employees.sort((a: any, b: any) => {
            const qeqData = this.qeqService['getQeqFromStorage']?.();

            let nameA = '';
            let nameB = '';

            if (qeqData && qeqData.data) {
              const qeqUserA = qeqData.data.find(
                (u: QeqUser) => u.personnelnumber === a.user?.username
              );
              const qeqUserB = qeqData.data.find(
                (u: QeqUser) => u.personnelnumber === b.user?.username
              );

              nameA = qeqUserA
                ? qeqUserA.name.toLowerCase()
                : a.user?.username?.toLowerCase() || '';
              nameB = qeqUserB
                ? qeqUserB.name.toLowerCase()
                : b.user?.username?.toLowerCase() || '';
            } else {
              // Fallback to username if QeQ data is not available
              nameA = a.user?.username?.toLowerCase() || '';
              nameB = b.user?.username?.toLowerCase() || '';
            }

            return nameA.localeCompare(nameB);
          });

          this.dataSource.data = sortedEmployees;
          this.dataLoaded = true;
          this.loaderService.hideLoader();
        },
        error: (err) => {
          console.error(err);
          this.loaderService.hideLoader();
        },
      });

    this.dataSource.paginator = this.paginator;
  }

  goToUser(external_id: number) {
    this.router.navigate(['/home/users/list/edit', external_id]);
    this.isUuid = true;
  }

  addUser() {
    this.router.navigate(['/home/users/list/add']);
    this.isUuid = true;
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.destroy$.next();
    this.destroy$.complete();

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
